// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-SHIP-23 — carrier service options. E-05: `api/client.ts` is frozen for new exports.
// E-19: the TS half of ServiceOptionsResponse in functions/Shipping/ShippingCarrierFunctions.cs.

import { apiFetch, parseApiError, type ShippingCarrier } from './client'
import { jsonRequest } from './http'
import { swrInvalidate } from './swrCache'

/** Platform operators act on another pharmacy by naming it; pharmacy users never pass one. */
const scoped = (path: string, pharmacyId?: string) =>
  pharmacyId ? `${path}${path.includes('?') ? '&' : '?'}pharmacy_id=${encodeURIComponent(pharmacyId)}` : path

export interface ServiceOption {
  code: string
  name: string
  /** null = availability unknown until a destination is chosen. */
  eligible: boolean | null
  price: number | null
  reason: string | null
}

export interface ServiceOptions {
  address_aware: boolean
  default_code: string | null
  detail: string | null
  options: ServiceOption[]
}

export interface ServiceOptionsInput {
  ship_to?: { line1: string; city: string; state: string; zip: string }
  require_signature?: boolean
  adult_signature?: boolean
  cold_chain?: boolean
  pickup_location_id?: string
}

export async function getCarrierServices(carrierId: string, input: ServiceOptionsInput, pharmacyId?: string): Promise<ServiceOptions> {
  const res = await apiFetch(scoped(`/shipping/carriers/${carrierId}/services`, pharmacyId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) throw await parseApiError(res)
  return res.json() as Promise<ServiceOptions>
}

// D-SHIP-23 §10 — pickup locations (RoadPas branches). E-19: PickupLocationsResponse in ShippingCarrierFunctions.cs.

export interface PickupLocation { id: string; name: string }

export interface PickupLocations {
  /** false = this carrier has no pickup choice (e-Courier, manual). */
  supported: boolean
  /** exactly one location: nothing to choose. */
  locked: boolean
  default_id: string | null
  detail: string | null
  locations: PickupLocation[]
}

export const getPickupLocations = (carrierId: string, pharmacyId?: string) =>
  jsonRequest<PickupLocations>('GET', scoped(`/shipping/carriers/${carrierId}/pickup-locations`, pharmacyId))

// Carrier accounts for one pharmacy (Settings → Shipping, and the SaaS-admin Shipping tab).

export interface CarrierAccountList { items: ShippingCarrier[]; count: number; supported_carriers: string[] }

export interface CarrierAccountInput {
  carrier?: string
  display_name?: string
  base_url?: string
  api_website?: string
  account_number?: string
  default_service_level?: string
  credentials?: Record<string, string>
  default_signature?: boolean
  default_cold_chain?: boolean
  is_test_mode?: boolean
  is_active?: boolean
  require_quote_confirm?: boolean
  send_cod?: boolean
}

export interface CarrierTestResult { ok: boolean; detail: string; token_hint: string | null; account_number: string | null }

export const listCarrierAccounts = (pharmacyId?: string) =>
  jsonRequest<CarrierAccountList>('GET', scoped('/shipping/carriers', pharmacyId))

/** GET /shipping/carriers is list-cached (swrCache); a write must drop it or the refetch returns the old list. */
const write = <T>(p: Promise<T>) => p.then(r => { swrInvalidate('/shipping/carriers'); return r })

export const createCarrierAccount = (input: CarrierAccountInput, pharmacyId?: string) =>
  write(jsonRequest<{ id: string; created: boolean; account_number: string | null }>('POST', scoped('/shipping/carriers', pharmacyId), input))

export const updateCarrierAccount = (id: string, input: CarrierAccountInput, pharmacyId?: string) =>
  write(jsonRequest<{ id: string; updated: boolean }>('PATCH', scoped(`/shipping/carriers/${id}`, pharmacyId), input))

/** Test can save an auto-detected branch and the verify time, so it invalidates too. */
export const testCarrierAccount = (id: string, pharmacyId?: string) =>
  write(jsonRequest<CarrierTestResult>('POST', scoped(`/shipping/carriers/${id}/test`, pharmacyId)))
